import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'ko' | 'en';

interface Translations {
  // Header
  systemTitle: string;
  systemSubtitle: string;
  connected: string;
  notifications: string;
  settings: string;
  profile: string;
  logout: string;
  logoutConfirm: string;
  viewAllNotifications: string;
  layoutReset: string;
  
  // ProfitStatus
  profitStatus: string;
  todaysProfit: string;
  weekly: string;
  monthly: string;
  profitTrend: string;
  
  // PositionStatus
  currentPosition: string;
  currentPrice: string;
  holdings: string;
  usdtValue: string;
  averageBuyPrice: string;
  unrealizedPnL: string;
  buy: string;
  sell: string;
  processing: string;
  
  // General
  loading: string;
  error: string;
  success: string;
  cancel: string;
  confirm: string;

  // Settings - AI Chatbot
  aiChatbotSettings: string;
  aiModelSelection: string;
  ruleBasedAI: string;
  chatgptAI: string;
  openaiApiKey: string;
  chatgptModel: string;
  systemPrompt: string;
  systemPromptPlaceholder: string;
  systemPromptDescription: string;
  openaiApiKeyDescription: string;
  saveSettings: string;
  saving: string;
  show: string;
  hide: string;

  // Settings Tabs
  chatbotTab: string;
  exchangeTab: string;
  aiServiceTab: string;
}

const translations: Record<Language, Translations> = {
  ko: {
    // Header
    systemTitle: 'exTrader',
    systemSubtitle: 'AI 자동거래 시스템',
    connected: '실시간 연결됨',
    notifications: '알림',
    settings: '설정',
    profile: '프로필',
    logout: '로그아웃',
    logoutConfirm: '정말 로그아웃하시겠습니까?',
    viewAllNotifications: '모든 알림 보기',
    layoutReset: '레이아웃 초기화',
    
    // ProfitStatus
    profitStatus: '수익 현황',
    todaysProfit: '오늘의 수익',
    weekly: '주간',
    monthly: '월간',
    profitTrend: '수익 추이',
    
    // PositionStatus
    currentPosition: '현재 포지션',
    currentPrice: '현재 가격',
    holdings: '보유량',
    usdtValue: 'USDT 가치',
    averageBuyPrice: '평균 매수가',
    unrealizedPnL: '미실현 손익',
    buy: '매수',
    sell: '매도',
    processing: '처리 중...',
    
    // General
    loading: '로딩 중...',
    error: '오류',
    success: '성공',
    cancel: '취소',
    confirm: '확인',

    // Settings - AI Chatbot
    aiChatbotSettings: 'AI 챗봇 설정',
    aiModelSelection: 'AI 모델 선택',
    ruleBasedAI: '규칙 기반 AI (무료, 기본)',
    chatgptAI: 'ChatGPT (고급, API 키 필요)',
    openaiApiKey: 'OpenAI API 키',
    chatgptModel: 'ChatGPT 모델',
    systemPrompt: '시스템 프롬프트',
    systemPromptPlaceholder: 'AI 어시스턴트의 역할과 응답 스타일을 정의하세요...',
    systemPromptDescription: 'ChatGPT가 어떤 역할로 응답할지 정의합니다.',
    openaiApiKeyDescription: 'OpenAI 웹사이트에서 API 키를 발급받아 입력하세요.',
    saveSettings: '설정 저장',
    saving: '저장 중...',
    show: '보기',
    hide: '숨기기',

    // Settings Tabs
    chatbotTab: 'AI 챗봇',
    exchangeTab: '거래소 API',
    aiServiceTab: 'AI 서비스',
  },
  en: {
    // Header
    systemTitle: 'exTrader',
    systemSubtitle: 'AI Auto Trading System',
    connected: 'Real-time Connected',
    notifications: 'Notifications',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    logoutConfirm: 'Are you sure you want to logout?',
    viewAllNotifications: 'View All Notifications',
    layoutReset: 'Reset Layout',
    
    // ProfitStatus
    profitStatus: 'Profit Status',
    todaysProfit: "Today's Profit",
    weekly: 'Weekly',
    monthly: 'Monthly',
    profitTrend: 'Profit Trend',
    
    // PositionStatus
    currentPosition: 'Current Position',
    currentPrice: 'Current Price',
    holdings: 'Holdings',
    usdtValue: 'USDT Value',
    averageBuyPrice: 'Average Buy Price',
    unrealizedPnL: 'Unrealized P&L',
    buy: 'Buy',
    sell: 'Sell',
    processing: 'Processing...',
    
    // General
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    confirm: 'Confirm',

    // Settings - AI Chatbot
    aiChatbotSettings: 'AI Chatbot Settings',
    aiModelSelection: 'AI Model Selection',
    ruleBasedAI: 'Rule-based AI (Free, Default)',
    chatgptAI: 'ChatGPT (Advanced, API Key Required)',
    openaiApiKey: 'OpenAI API Key',
    chatgptModel: 'ChatGPT Model',
    systemPrompt: 'System Prompt',
    systemPromptPlaceholder: 'Define the role and response style of the AI assistant...',
    systemPromptDescription: 'Defines how ChatGPT should respond.',
    openaiApiKeyDescription: 'Get an API key from the OpenAI website and enter it here.',
    saveSettings: 'Save Settings',
    saving: 'Saving...',
    show: 'Show',
    hide: 'Hide',

    // Settings Tabs
    chatbotTab: 'AI Chatbot',
    exchangeTab: 'Exchange API',
    aiServiceTab: 'AI Services',
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('ko');

  const value = {
    language,
    setLanguage,
    t: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 